import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import type { TokenPhase } from '@/lib/demo/types';

/* ────────────────────────────────────────────────────────────────────────────
   Badges. Tinted hairline chips — never solid fills, so they read as metadata
   rather than as buttons.
   ──────────────────────────────────────────────────────────────────────────── */

export type BadgeVariant = 'neutral' | 'bull' | 'floor' | 'up' | 'down' | 'warn' | 'info';
export type BadgeSize = 'sm' | 'md';

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  /** Leading status pip in the badge's own colour. */
  dot?: boolean;
  /** Makes the pip breathe — use only for genuinely live states. */
  pulse?: boolean;
  children?: ReactNode;
}

const VARIANTS: Record<BadgeVariant, string> = {
  neutral: 'border-line bg-elevated text-muted',
  bull: 'border-bull/30 bg-bull/10 text-bull',
  floor: 'border-floor/35 bg-floor/10 text-floor',
  up: 'border-up/30 bg-up/10 text-up',
  down: 'border-down/30 bg-down/10 text-down',
  warn: 'border-warn/35 bg-warn/10 text-warn',
  info: 'border-info/35 bg-info/10 text-info',
};

const SIZES: Record<BadgeSize, string> = {
  sm: 'h-5 gap-1 px-1.5 text-3xs',
  md: 'h-6 gap-1.5 px-2 text-2xs',
};

export function Badge({
  variant = 'neutral',
  size = 'md',
  dot = false,
  pulse = false,
  className,
  children,
  ...rest
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center whitespace-nowrap rounded-md border font-medium uppercase tracking-[0.1em]',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {dot ? (
        <span
          aria-hidden="true"
          className={cn(
            'inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-current',
            pulse && 'animate-pulse',
          )}
        />
      ) : null}
      {children}
    </span>
  );
}

interface PhaseSpec {
  label: string;
  variant: BadgeVariant;
  dot: boolean;
  pulse: boolean;
}

/** Canonical phase vocabulary. Never invent a synonym for these five states. */
const PHASES: Record<TokenPhase, PhaseSpec> = {
  launching: { label: 'Launching', variant: 'warn', dot: true, pulse: true },
  trading: { label: 'Trading', variant: 'bull', dot: true, pulse: false },
  graduating: { label: 'Graduating', variant: 'info', dot: true, pulse: true },
  graduated: { label: 'Graduated', variant: 'floor', dot: false, pulse: false },
  halted: { label: 'Halted', variant: 'down', dot: true, pulse: false },
};

export interface PhaseBadgeProps extends Omit<BadgeProps, 'variant' | 'children' | 'dot' | 'pulse'> {
  phase: TokenPhase;
}

export function PhaseBadge({ phase, size = 'sm', className, ...rest }: PhaseBadgeProps) {
  const spec = PHASES[phase] ?? PHASES.trading;
  return (
    <Badge
      variant={spec.variant}
      size={size}
      dot={spec.dot}
      pulse={spec.pulse}
      className={className}
      {...rest}
    >
      {spec.label}
    </Badge>
  );
}

/** Exposed so pages can label a phase without rendering the chip. */
export function phaseLabel(phase: TokenPhase): string {
  return (PHASES[phase] ?? PHASES.trading).label;
}
